import React, { useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';

import FormRow from '../../../app/components/FormRow';
import ValidationAlert from '../../../app/components/ValidationAlert';
import { useTranslation } from '../../../common/hooks/useTranslation';
import momencik from '../../../common/momencik';
import { validate } from '../../../common/pokCore/contexts/SalesInvoiceOrderNumberContext';
import { CreateUpdateSalesInvoiceOrderNumberDto } from '../../../common/pokCore/autogenerated/pokApiClient';

interface SalesInvoiceOrderNumberEditorProps {
  show: boolean;
  dto: CreateUpdateSalesInvoiceOrderNumberDto;
  title?: string;
  onSave: (dto: CreateUpdateSalesInvoiceOrderNumberDto) => void;
  onClose: () => void;
}

export const SalesInvoiceOrderNumberEditor: React.FC<
  SalesInvoiceOrderNumberEditorProps
> = ({ show, dto: initialDto, title, onSave, onClose }) => {
  const { t, tk } = useTranslation('fvsLng');
  const [dto, setDto] =
    useState<CreateUpdateSalesInvoiceOrderNumberDto>(initialDto);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [showSaveErrors, setShowSaveErrors] = useState(false);

  const propertyChange = (
    obj: Partial<CreateUpdateSalesInvoiceOrderNumberDto>,
  ) => {
    setDto(prev => ({ ...prev, ...obj }));
  };

  const handleSave = async () => {
    const validation = await validate(dto);
    if (!validation.valid) {
      setSaveErrors(validation.errors);
      setShowSaveErrors(true);
      return;
    }
    setSaveErrors([]);
    setShowSaveErrors(false);
    onSave(dto);
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{title ?? t(tk.finance.addOrderNumber)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form className="d-grid gap-3">
          <FormRow controlId="orderNumber" label={t(tk.finance.orderNumber)}>
            <Form.Control
              value={dto.orderNumber}
              onChange={e => propertyChange({ orderNumber: e.target.value })}
              autoFocus
            />
          </FormRow>
          <FormRow controlId="orderDate" label={t(tk.finance.orderDate)}>
            <Form.Control
              type="date"
              value={dto.orderDate ? momencik(dto.orderDate, 'YYYY-MM-DD') : ''}
              onChange={e =>
                propertyChange({
                  orderDate: e.target.value ? new Date(e.target.value) : null,
                })
              }
            />
          </FormRow>
        </Form>
        <ValidationAlert
          show={showSaveErrors}
          errors={saveErrors}
          className="mt-3"
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>
          {t(tk.button.cancel)}
        </Button>
        <Button variant="primary" onClick={handleSave}>
          {t(tk.button.save)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
